import { captureProblem, type CaptureProblem, type CaptureSupport } from '@/core/recording/session';

/** What this browser can capture. Recording also needs WebCodecs to encode. */
export function captureSupport(): CaptureSupport {
  const media = typeof navigator === 'undefined' ? undefined : navigator.mediaDevices;
  const encodes = typeof VideoEncoder !== 'undefined';
  return {
    screen: encodes && typeof media?.getDisplayMedia === 'function',
    devices: encodes && typeof media?.getUserMedia === 'function',
  };
}

/** A capture that failed, with the reason the UI explains. */
export class CaptureError extends Error {
  constructor(
    readonly problem: CaptureProblem,
    readonly source: 'screen' | 'microphone' | 'camera',
    cause?: unknown,
  ) {
    super(`${source}: ${problem}`, { cause });
    this.name = 'CaptureError';
  }
}

function toCaptureError(error: unknown, source: CaptureError['source']): CaptureError {
  if (error instanceof CaptureError) return error;
  const { name, message } = error instanceof Error ? error : { name: undefined, message: '' };
  return new CaptureError(captureProblem(name, source === 'screen' ? 'screen' : 'device', message), source, error);
}

/**
 * Chrome options that keep the person in control: never offer this tab (it
 * would record itself in a loop), allow switching what is shared mid-recording,
 * and offer system audio where the platform has it.
 */
type DisplayOptions = DisplayMediaStreamOptions & {
  readonly controller?: unknown;
  readonly selfBrowserSurface?: 'include' | 'exclude';
  readonly surfaceSwitching?: 'include' | 'exclude';
  readonly systemAudio?: 'include' | 'exclude';
  readonly monitorTypeSurfaces?: 'include' | 'exclude';
};

/**
 * Opens the browser's screen picker. Audio is the shared tab's or system sound.
 * Capped at 1440p: sharp text for 1080p results, without asking more of the
 * encoder than most computers can do live alongside a camera.
 */
export async function captureScreen(withAudio: boolean, controller?: unknown): Promise<MediaStream> {
  const options: DisplayOptions = {
    video: { frameRate: { ideal: 30, max: 30 }, width: { max: 2560 }, height: { max: 1440 } },
    audio: withAudio,
    controller,
    selfBrowserSurface: 'exclude',
    surfaceSwitching: 'include',
    systemAudio: 'include',
    monitorTypeSurfaces: 'include',
  };
  try {
    const stream = await navigator.mediaDevices.getDisplayMedia(options);
    // Screens are mostly text and UI: tell the encoder to keep edges sharp.
    for (const track of stream.getVideoTracks()) track.contentHint = 'detail';
    return stream;
  } catch (error) {
    throw toCaptureError(error, 'screen');
  }
}

export async function openMicrophone(deviceId?: string): Promise<MediaStream> {
  try {
    return await navigator.mediaDevices.getUserMedia({
      audio: { deviceId: deviceId ? { exact: deviceId } : undefined, echoCancellation: true, noiseSuppression: true, autoGainControl: true },
    });
  } catch (error) {
    throw toCaptureError(error, 'microphone');
  }
}

export async function openCamera(deviceId?: string): Promise<MediaStream> {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      video: { deviceId: deviceId ? { exact: deviceId } : undefined, width: { ideal: 1920 }, height: { ideal: 1080 }, frameRate: { ideal: 30, max: 30 } },
    });
    for (const track of stream.getVideoTracks()) track.contentHint = 'motion';
    return stream;
  } catch (error) {
    throw toCaptureError(error, 'camera');
  }
}

export interface DeviceChoice {
  readonly id: string;
  readonly label: string;
}

export interface DeviceList {
  readonly microphones: readonly DeviceChoice[];
  readonly cameras: readonly DeviceChoice[];
}

/** Microphones and cameras; labels are only known once access was granted. */
export async function listDevices(): Promise<DeviceList> {
  const devices = await navigator.mediaDevices.enumerateDevices().catch(() => [] as MediaDeviceInfo[]);
  const pick = (kind: MediaDeviceKind) =>
    devices
      .filter((device) => device.kind === kind && device.deviceId && device.deviceId !== 'communications')
      .map((device, index) => ({ id: device.deviceId, label: device.label || `${index + 1}` }));
  return { microphones: pick('audioinput'), cameras: pick('videoinput') };
}

/** Turns the devices off (and the camera light with them). */
export function stopStream(stream: MediaStream | null | undefined): void {
  for (const track of stream?.getTracks() ?? []) track.stop();
}

/** The shared surface: a tab, a window or a whole screen (Chrome only). */
export function displaySurface(stream: MediaStream): 'browser' | 'window' | 'monitor' | null {
  const surface = (stream.getVideoTracks()[0]?.getSettings() as MediaTrackSettings & { displaySurface?: string } | undefined)?.displaySurface;
  return surface === 'browser' || surface === 'window' || surface === 'monitor' ? surface : null;
}

/** Chrome's `CaptureController`, which decides whether the shared tab or window takes focus. */
export function createCaptureController(): { setFocusBehavior?(behavior: 'focus-captured-surface' | 'no-focus-change'): void } | undefined {
  const Controller = (globalThis as { CaptureController?: new () => { setFocusBehavior?(behavior: string): void } }).CaptureController;
  return Controller ? new Controller() : undefined;
}
